import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import { toast } from "react-toastify";

import {
  systemChangeAffectedCreateApiCall,
  systemChangeAffectedUpdateApiCall,
  systemChangeAffectedDeleteApiCall,
  systemChangeAffectedLookupApiCall,
} from "../../../../helpers/fakebackend_helper";
import { formatDate } from "./shared";

/**
 * Change #100028 - "need ability to link company affected in here and the user.
 * Say for example user Linda reports it from company 10, we need to log it so we
 * remember to tell them it's fixed."
 *
 * The companies / users a change affects, each with a note and a "told" flag.
 * The list page shows a person icon on any change with rows here.
 */

const emptyForm = { company: null, user: null, contactName: "", notes: "" };

// Search-as-you-type against /systemchangeaffected/lookup (companies + users
// together; each picker keeps the half it needs).
const useLookup = (kind, enabled) => {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    // Only while the add form is open - opening a change should not search.
    if (!enabled) return undefined;
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await systemChangeAffectedLookupApiCall([{ Keyword: input.trim() }]);
        if (response?.status === 200) {
          setOptions(kind === "company" ? response.company_list || [] : response.user_list || []);
        }
      } catch (e) {
        // A failed search just leaves the old options; typing again retries.
      }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [input, kind, enabled]);

  return { input, setInput, options, loading };
};

const AffectedSection = ({ change, rows, ready, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const companyLookup = useLookup("company", adding);
  const userLookup = useLookup("user", adding);

  const pending = rows.filter((row) => Number(row.FlagNotified) !== 1).length;

  // Offer the reporter (Report a Bug fills these) unless they're already listed.
  const reporter = useMemo(() => {
    if (!change?.ReportedUser_Id && !change?.ReportedCompany_Id) return null;
    const already = rows.some(
      (row) =>
        (change.ReportedUser_Id && String(row.User_Id) === String(change.ReportedUser_Id)) ||
        (!change.ReportedUser_Id && String(row.Company_Id) === String(change.ReportedCompany_Id))
    );
    return already ? null : change;
  }, [change, rows]);

  const create = async (payload) => {
    setBusy(true);
    try {
      const response = await systemChangeAffectedCreateApiCall([
        { SystemChange_Id: change.Id, ...payload },
      ]);
      if (response?.status === 200) {
        toast.success("Added - they'll be on the list to tell");
        setForm(emptyForm);
        setAdding(false);
        onChanged();
      } else {
        toast.error(response?.message || "Could not add them");
      }
    } catch (e) {
      toast.error("Could not add them");
    }
    setBusy(false);
  };

  const update = async (row, patch, message) => {
    try {
      const response = await systemChangeAffectedUpdateApiCall([{ Id: row.Id, ...patch }]);
      if (response?.status === 200) {
        if (message) toast.success(message);
        onChanged();
      } else {
        toast.error(response?.message || "Could not update");
      }
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const remove = async (row) => {
    try {
      const response = await systemChangeAffectedDeleteApiCall({ Id: row.Id });
      if (response?.status === 200) onChanged();
      else toast.error(response?.message || "Could not remove");
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const canSave = !!(form.company || form.user || form.contactName.trim());

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Who to tell when fixed ({rows.length})
          {pending > 0 && rows.length !== pending && (
            <Typography component="span" variant="body2" color="text.secondary">
              {" "}
              · {pending} still to tell
            </Typography>
          )}
        </Typography>
        {ready && !adding && (
          <Button size="small" startIcon={<PersonAddAltIcon />} onClick={() => setAdding(true)}>
            Add
          </Button>
        )}
      </Box>

      {!ready && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Run the SystemChangeAffected database delta (#136) to start recording who to tell.
        </Alert>
      )}

      {ready && reporter && !adding && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PersonAddAltIcon />}
          disabled={busy}
          sx={{ mb: 1 }}
          onClick={() =>
            create({
              User_Id: reporter.ReportedUser_Id || null,
              Company_Id: reporter.ReportedCompany_Id || null,
              Notes: "Reported it",
            })
          }
        >
          Add the reporter
          {reporter.ReportedUser_FullName ? ` - ${reporter.ReportedUser_FullName}` : ""}
          {reporter.ReportedCompany_BusinessName
            ? ` (${reporter.ReportedCompany_BusinessName})`
            : reporter.ReportedCompany_Id
            ? ` (company ${reporter.ReportedCompany_Id})`
            : ""}
        </Button>
      )}

      {ready && adding && (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            p: 1.5,
            mb: 1.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Autocomplete
            size="small"
            options={companyLookup.options}
            loading={companyLookup.loading}
            value={form.company}
            onChange={(e, value) => setForm((prev) => ({ ...prev, company: value }))}
            inputValue={companyLookup.input}
            onInputChange={(e, value) => companyLookup.setInput(value)}
            filterOptions={(options) => options}
            isOptionEqualToValue={(a, b) => String(a.Id) === String(b.Id)}
            getOptionLabel={(option) => `${option.BusinessName || "Company"} (#${option.Id})`}
            renderInput={(params) => (
              <TextField {...params} label="Company" placeholder="Search by name or #id" />
            )}
          />
          <Autocomplete
            size="small"
            options={userLookup.options}
            loading={userLookup.loading}
            value={form.user}
            onChange={(e, value) => setForm((prev) => ({ ...prev, user: value }))}
            inputValue={userLookup.input}
            onInputChange={(e, value) => userLookup.setInput(value)}
            filterOptions={(options) => options}
            isOptionEqualToValue={(a, b) => String(a.Id) === String(b.Id)}
            getOptionLabel={(option) =>
              `${option.UserFullName || "User"}${option.Email ? ` - ${option.Email}` : ""}`
            }
            renderInput={(params) => (
              <TextField {...params} label="User" placeholder="Search by name, email or #id" />
            )}
          />
          <TextField
            size="small"
            label="Name (if not a CRMit user)"
            value={form.contactName}
            onChange={(e) => setForm((prev) => ({ ...prev, contactName: e.target.value }))}
          />
          <TextField
            size="small"
            label="Note"
            placeholder="e.g. rang in about it, prefers email"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setAdding(false);
                setForm(emptyForm);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!canSave || busy}
              onClick={() =>
                create({
                  Company_Id: form.company?.Id || null,
                  User_Id: form.user?.Id || null,
                  ContactName: form.contactName.trim() || null,
                  Notes: form.notes.trim() || null,
                })
              }
            >
              Add
            </Button>
          </Box>
        </Box>
      )}

      {ready && rows.length === 0 && !adding && (
        <Typography color="text.secondary">Nobody recorded yet</Typography>
      )}

      {rows.map((row) => {
        const told = Number(row.FlagNotified) === 1;
        const who =
          row.User_FullName || row.ContactName || (row.User_Id ? `User #${row.User_Id}` : "");
        const company =
          row.Company_BusinessName || (row.Company_Id ? `Company #${row.Company_Id}` : "");
        return (
          <Box
            key={row.Id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              py: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <PersonOutlineIcon
              fontSize="small"
              color={told ? "disabled" : "primary"}
              sx={{ mt: 0.25 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {who || company}
                {who && company && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" "}
                    · {company}
                  </Typography>
                )}
              </Typography>
              {row.User_Email && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {row.User_Email}
                </Typography>
              )}
              {row.Notes && (
                <Typography variant="caption" display="block">
                  {row.Notes}
                </Typography>
              )}
            </Box>
            {told ? (
              <Tooltip title="Undo - they still need telling">
                <Chip
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon />}
                  label={`Told ${formatDate(row.NotifiedDateTime)}${
                    row.NotifiedUser_FullName ? ` by ${row.NotifiedUser_FullName.split(" ")[0]}` : ""
                  }`}
                  onDelete={() => update(row, { FlagNotified: 0 })}
                  deleteIcon={<UndoIcon />}
                />
              </Tooltip>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => update(row, { FlagNotified: 1 }, `Marked ${who || company} as told`)}
              >
                Mark told
              </Button>
            )}
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => remove(row)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
};

export default AffectedSection;
